
// This file contains all the English translations.
// Last updated: [DATE]
export default {
  translation: {
    landing: {
      nav: {
        features: 'Features',
        workflow: 'Workflow',
        why_us: 'Why Us',
        pricing: 'Pricing',
        login: 'Log in',
        get_started: 'Get Started'
      },
      hero: {
        new_feature: 'New: Advanced Loyalty System',
        title_start: 'The Complete Operating System',
        title_gradient: 'for Growing Restaurants',
        subtitle: 'Manage orders, inventory, staff, and loyalty in one unified platform. Built for speed, reliability, and growth.',
        start_trial: 'Start Free Trial',
        view_demo: 'Watch Workflow',
        no_card: 'No credit card required'
      },
      social_proof: {
        trusted_by: 'Trusted by 500+ forward-thinking restaurants'
      },
      core_features: {
        title: 'Core Capabilities',
        subtitle: 'Everything you need to run your restaurant efficiently.',
        orders: 'Order Management',
        inventory: 'Inventory Control',
        qr: 'QR Menus',
        menu: 'Menu Management',
        branches: 'Multi-Branch',
        analytics: 'Analytics',
        accounting: 'Accounting',
        users: 'Users & Roles',
        loyalty: 'Loyalty Program'
      },
      sections: {
        orders: {
          title: 'Streamlined Order Management',
          desc: 'Handle dine-in, takeaway, and delivery orders from a single screen. Send orders directly to the kitchen with zero errors.',
          feat1: 'Real-time KDS integration',
          feat2: 'Table status tracking',
          feat3: 'Split bill & merge tables'
        },
        inventory: {
          title: 'Smart Inventory Tracking',
          desc: 'Stop running out of ingredients. Track stock levels in real-time and get low-stock alerts automatically.',
          feat1: 'Recipe costing',
          feat2: 'Supplier management',
          feat3: 'Automatic stock deduction'
        },
        qr_menu: {
          title: 'Dynamic QR Codes',
          desc: 'Create beautiful, contactless menus that you can update instantly. No more printing costs.',
          feat1: 'Multi-language support',
          feat2: 'Scheduled menus',
          feat3: 'Direct ordering from table'
        },
        multi_branch: {
          title: 'Multi-Branch Control',
          desc: 'Manage all your locations from one master dashboard. Push menu updates to all branches instantly.',
          feat1: 'Centralized reporting',
          feat2: 'Branch-specific pricing',
          feat3: 'Unified staff management'
        },
        analytics: {
          title: 'Powerful Analytics',
          desc: 'Make data-driven decisions with comprehensive reports on sales, staff performance, and inventory.',
          feat1: 'Sales trends',
          feat2: 'Best selling items',
          feat3: 'Peak hour analysis'
        },
        accounting: {
          title: 'Integrated Accounting',
          desc: 'Simplify your finances. Track expenses, profit & loss, and tax reports directly within the system.',
          feat1: 'Expense tracking',
          feat2: 'Profit & Loss reports',
          feat3: 'Tax calculation'
        },
        users: {
          title: 'Users & Permissions',
          desc: 'Secure your data with granular role-based access control. Give staff access only to what they need.',
          feat1: 'Custom roles',
          feat2: 'Activity logs',
          feat3: 'Secure pin access'
        },
        loyalty: {
          title: 'Loyalty Points System',
          desc: 'Turn one-time guests into regulars. Reward customers with points for every purchase.',
          feat1: 'Digital loyalty cards',
          feat2: 'Redeemable rewards',
          feat3: 'Customer database'
        }
      },
      why_us: {
        title: 'Why Choose Ripplefy?',
        subtitle: 'We focus on what matters most to restaurant owners.',
        benefit1: {
          title: 'All-in-One Solution',
          desc: 'Replace 5+ different tools with one integrated platform.'
        },
        benefit2: {
          title: 'Built for Speed',
          desc: 'Optimized for fast-paced environments. Zero lag.'
        },
        benefit3: {
          title: '24/7 Support',
          desc: 'Our team is always ready to help you succeed.'
        }
      },
      workflow: {
        title: 'Your Daily Workflow, Simplified',
        subtitle: 'See how Ripplefy fits perfectly into your operation.',
        step1: 'Customer scans QR code',
        step2: 'Order sent to Kitchen (KDS)',
        step3: 'Staff serves food',
        step4: 'Payment & Loyalty points added'
      },
      pricing: {
        title: 'Simple Pricing',
        subtitle: 'Start small and scale as you grow.',
        starter: 'Starter',
        pro: 'Professional',
        enterprise: 'Enterprise',
        currency: '$',
        month: '/mo',
        cta: 'Choose Plan'
      },
      cta: {
        title: 'Ready to transform your restaurant?',
        subtitle: 'Join the revolution in restaurant management.',
        button: 'Get Started Now'
      },
      footer: {
        rights: 'All rights reserved.'
      }
    },
    dashboard: {
      title: 'Ripplefy',
      navigation: {
        overview: 'Overview',
        business_profile: 'Business Profile',
        branches: 'Branches',
        menu_management: 'Menu Management',
        orders: 'Orders',
        pos_system: 'POS System',
        staff_management: 'Staff Management',
        loyalty_program: 'Loyalty Program',
        customization: 'Customization',
        menu_builder: 'Menu Builder',
        menu_designer: 'Menu Designer',
        analytics: 'Analytics',
        advanced_analytics: 'Advanced Analytics',
        feedback: 'Feedback',
        audit_logs: 'Audit Logs',
        qr_codes: 'QR Codes',
        tables: 'Tables',
        kitchen: 'Kitchen Display',
        meta_pixel: 'Meta Pixel',
        settings: 'Settings',
        reservations: 'Reservations',
        waiting_list: 'Waiting List',
        delivery_management: 'Delivery Management',
        inventory: 'Inventory',
        customer_communication: 'Customer Communication',
        marketing_promotions: 'Marketing & Promotions',
        financial_reports: 'Financial Reports',
        subscription: 'Subscription',
        sections: {
          overview: 'Overview',
          operations: 'Operations',
          management: 'Management',
          analytics: 'Analytics & Reports',
          customization: 'Customization & Settings'
        }
      },
      logout: 'Sign Out',
      language: 'Language',
      overview: {
        title: 'Dashboard Overview',
        subtitle: 'Welcome to your restaurant management platform',
        welcome_back: 'Welcome back, {{name}}!',
        welcome: 'Welcome to your restaurant management platform',
        stat_cards: {
          restaurant: 'Restaurant',
          restaurant_desc: 'No restaurant set up',
          total_branches: 'Total Branches',
          total_branches_desc: 'Active locations',
          menu_items: 'Menu Items',
          menu_items_desc: 'Across all branches',
          qr_code_scans: 'QR Code Scans',
          qr_code_scans_desc: 'This month',
        },
        quick_actions: {
          title: 'Quick Actions',
          desc_manage: 'Manage your restaurant and branches',
          desc_get_started: 'Get started with your restaurant',
          manage_restaurant: 'Manage Restaurant',
          menu_management: 'Menu Management',
          customize_menu: 'Customize Menu',
          setup_needed: 'You need to complete the restaurant setup first.',
          complete_setup: 'Complete Setup'
        },
        recent_activity: {
          title: 'Recent Activity',
          no_activity: 'No recent activity',
          activity_appears_here: 'Activity will appear here',
          complete_setup_to_see_activity: 'Complete setup to see activity'
        },
      },
      home: {
        staff_dashboard: 'Staff Dashboard',
        take_a_tour: 'Take a Tour',
        total_branches: 'Total Branches',
        active_locations: 'Active locations',
        pending_orders: 'Pending Orders',
        awaiting_processing: 'Awaiting processing',
        action_needed: 'Action needed',
        total_orders: 'Total Orders',
        all_time_orders: 'All time orders',
        core_operations: 'Core Operations',
        core_operations_desc: 'Quick access to your daily operations and management tools',
        advanced_features: 'Advanced Features',
        get_started: 'Get Started',
        complete_setup_unlock: 'Complete your setup to unlock all features',
        business_profile: 'Business Profile',
        create_branch: 'Create Branch',
        setup_menu: 'Setup Menu',
        manage_orders: 'Manage Orders',
        track_fulfill_orders: 'Track & fulfill orders',
        update_menu_items: 'Update menu items',
        qr_codes: 'QR Codes',
        generate_qr_codes: 'Generate table QR codes',
        loyalty_program: 'Loyalty Program',
        customer_rewards: 'Customer rewards',
        advanced_analytics: 'Advanced Analytics',
        detailed_insights: 'Detailed insights',
        customize_menu: 'Customize Menu',
        branding_layout: 'Branding & layout'
      },
      business_profile: {
        title: 'Business Profile',
        description: 'Manage your restaurant\'s details and branches.',
        edit_branch_dialog: {
          title: 'Edit Branch',
          description: 'Update your branch information and settings.',
          restaurant_name_label: 'Restaurant Name',
          restaurant_name_placeholder: 'Enter restaurant name',
          restaurant_description_label: 'Restaurant Description',
          restaurant_description_placeholder: 'Enter a description for your restaurant',
          branch_name_label: 'Branch Name',
          branch_name_placeholder: 'Enter branch name',
          slug_label: 'URL Slug',
          slug_description: 'This will be used in your menu URL',
          address_label: 'Address',
          address_placeholder: 'Enter branch address',
          phone_label: 'Phone Number',
          phone_placeholder: 'Enter phone number',
          whatsapp_label: 'WhatsApp',
          whatsapp_placeholder: 'Enter WhatsApp number',
          instagram_label: 'Instagram',
          instagram_placeholder: 'Enter Instagram handle',
          facebook_label: 'Facebook',
          facebook_placeholder: 'Enter Facebook page URL',
          website_label: 'Website',
          website_placeholder: 'Enter website URL',
          save_button: 'Save Changes',
          cancel_button: 'Cancel',
          toast_success_title: 'Success',
          toast_success_description: 'Branch updated successfully',
          toast_error_title: 'Error',
          toast_error_description: 'Failed to update branch',
          toast_error_slug_taken: 'This URL slug is already taken',
          toast_error_failed: 'An error occurred while updating the branch'
        },
        create_branch_dialog: {
          title: 'Create New Branch',
          name_label: 'Branch Name',
          name_placeholder: 'Enter branch name',
          address_label: 'Address',
          address_placeholder: 'Enter branch address',
          phone_label: 'Phone Number',
          phone_placeholder: 'Enter phone number',
          whatsapp_label: 'WhatsApp',
          instagram_label: 'Instagram',
          instagram_placeholder: 'Enter Instagram handle',
          facebook_label: 'Facebook',
          facebook_placeholder: 'Enter Facebook page URL',
          website_label: 'Website',
          website_placeholder: 'Enter website URL',
          cancel_button: 'Cancel',
          create_button: 'Create Branch',
          creating_button: 'Creating...',
          toast_success_title: 'Success',
          toast_success_description: 'Branch created successfully',
          toast_error_title: 'Error',
          toast_error_name_required: 'Branch name is required',
          toast_error_failed: 'Failed to create branch'
        },
        restaurant_info: {
          title: 'Restaurant Information',
          description: 'Manage your restaurant\'s basic information and branding.',
          edit_button: 'Edit',
          name_label: 'Restaurant Name *',
          phone_label: 'Phone Number',
          description_label: 'Description',
          logo_label: 'Restaurant Logo',
          save_button: 'Save Changes',
          saving_button: 'Saving...',
          cancel_button: 'Cancel',
          no_description: 'No description provided.'
        },
        branches: {
          title: 'Branch Locations',
          description: 'Manage your restaurant locations and their individual settings.',
          add_button: 'Add Branch',
          no_branches: 'No branches have been added yet.',
          view_live_button: 'View Live'
        },
        toasts: {
          success_title: 'Success',
          success_description: 'Restaurant profile updated successfully!',
          error_title: 'Error',
          error_description: 'Failed to update restaurant profile',
          branch_created_success: 'Branch created successfully!'
        },
        no_restaurant: 'No restaurant found. Please complete the onboarding process.',
        branches_page: {
          title: 'Branch Management',
          description: 'Manage all your restaurant locations from one place.',
          add_button: 'Add New Branch',
          all_branches: 'All Branches ({{count}})',
          all_branches_desc: 'A list of all branches for your restaurant.',
          no_branches_title: 'No branches yet',
          no_branches_desc: 'Click "Add New Branch" to create your first location.',
          active: 'Active',
          inactive: 'Inactive',
          open_menu: 'Open menu',
          edit_button: 'Edit',
          view_live_menu: 'View Live Menu'
        },
      },
      inventory: {
        title: 'Inventory Management',
        description: 'Manage your raw materials, track stock levels, and analyze costs.',
        tabs: {
          overview: 'Overview',
          materials: 'Materials',
          recipes: 'Recipes',
          analytics: 'Analytics',
          expenses: 'Expenses'
        },
        alerts: {
          stock_alerts_message: 'You have stock alerts that need attention',
          out_of_stock: 'out of stock',
          low_stock: 'low stock',
          view_materials: 'View Materials'
        },
        empty_states: {
          select_branch: 'Select a branch',
          select_branch_desc: 'Choose a branch to view inventory data',
          materials_desc: 'Choose a branch to manage materials',
          recipes_desc: 'Choose a branch to manage recipes',
          analytics_desc: 'Choose a branch to view analytics',
          expenses_desc: 'Choose a branch to manage expenses'
        },

        dashboard: {
          beta_label: 'Beta',
          currently_viewing: 'Currently viewing',
          select_branch: 'Select Branch',
          select_branch_prompt: 'Please select a branch to view inventory data',
          no_branches: 'No branches available. Please create a branch first.',
          roadmap: {
            title: 'Inventory Management Roadmap',
            description: 'See what features are available and what\'s coming soon',
            available_now: 'Available Now',
            coming_soon: 'Coming Soon',
            future: 'Future Plans',
            features: {
              materials: 'Material management',
              recipes: 'Recipe creation & management',
              analytics: 'Advanced analytics & insights',
              expenses: 'Expense tracking',
              cost_tracking: 'Cost tracking & analysis',
              waste_analysis: 'Waste analysis & recommendations',
              purchase_orders: 'Purchase order management',
              suppliers: 'Supplier management',
              barcode: 'Barcode scanning',
              notifications: 'Stock alerts & notifications',
              mobile_app: 'Mobile inventory app',
              ai_forecasting: 'AI-powered demand forecasting',
              pos_integration: 'POS system integration',
              realtime_tracking: 'Real-time inventory tracking'
            }
          }
        },
        dashboard_old: {
          title: 'Dashboard',
          quick_stats: {
            total_materials: 'Total Materials',
            total_value: 'Total Value',
            low_stock: 'Low Stock',
            out_of_stock: 'Out of Stock',
            average_cost: 'Average Cost',
            monthly_spending: 'Monthly Spending',
            monthly_savings: 'Monthly Savings',
            stock_turnover: 'Stock Turnover Rate'
          },
          roadmap: {
            title: 'Roadmap',
            analytics_available: 'Analytics & Reports - Available Now',
            analytics_desc: 'Comprehensive analytics, detailed reports, and cost analysis',
            purchase_orders: 'Purchase Orders - Coming Soon',
            purchase_orders_desc: 'Automated purchase order management and supplier integration',
            supplier_management: 'Supplier Management - Coming Soon',
            supplier_management_desc: 'Manage suppliers, track deliveries, and negotiate prices'
          }
        },
        analytics: {
          title: 'Inventory Analytics',
          description: 'Comprehensive inventory analytics and insights',
          no_branch_selected: 'Please select a branch to view analytics',
          time_range: {
            label: 'Time Range',
            last_7_days: 'Last 7 Days',
            last_30_days: 'Last 30 Days',
            last_90_days: 'Last 90 Days',
            last_6_months: 'Last 6 Months',
            last_year: 'Last Year'
          },
          tabs: {
            overview: 'Overview',
            financial: 'Financial',
            waste: 'Waste',
            usage: 'Usage',
            recipes: 'Recipes'
          },
          metrics: {
            revenue: 'Revenue',
            cogs: 'COGS',
            of_revenue: 'of revenue',
            net_profit: 'Net Profit',
            margin: 'margin',
            inventory_value: 'Inventory Value',
            materials: 'materials',
            stock_turnover: 'Stock Turnover',
            stock_turnover_desc: 'Times inventory sold per period',
            waste_percentage: 'Waste Percentage',
            total_waste: 'total waste',
            stock_alerts: 'Stock Alerts',
            out_of_stock: 'Out of Stock',
            low_stock: 'Low Stock',
            total_value: 'Total Value',
            total_materials: 'Total Materials',
            monthly_spending: 'Monthly Spending',
            stock_turnover_rate: 'Stock Turnover Rate',
            low_stock_count: 'Low Stock Items',
            out_of_stock_count: 'Out of Stock'
          },
          charts: {
            top_used_materials: 'Top Used Materials',
            top_used_materials_desc: 'Materials with highest consumption this period',
            top_materials_by_value: 'Top Materials by Value',
            stock_levels: 'Stock Levels',
            transaction_history: 'Transaction History'
          },
          alerts: {
            stock_alerts_title: 'Stock Alerts',
            items: 'items'
          },
          financial: {
            revenue_breakdown: 'Revenue Breakdown',
            total_revenue: 'Total Revenue',
            cogs: 'Cost of Goods Sold',
            gross_profit: 'Gross Profit',
            expenses: 'Operating Expenses',
            net_profit: 'Net Profit',
            profit_margins: 'Profit Margins',
            gross_margin: 'Gross Profit Margin',
            gross_margin_desc: 'Revenue minus COGS',
            net_margin: 'Net Profit Margin',
            net_margin_desc: 'Profit after all expenses',
            cogs_ratio: 'COGS Ratio',
            cogs_ratio_desc: 'Food cost percentage'
          },
          waste: {
            total_waste: 'Total Waste',
            total_units: 'units wasted',
            waste_cost: 'Waste Cost',
            of_purchases: 'of purchases',
            significant_waste_items: 'Significant Waste',
            items_over_threshold: 'items over threshold',
            high_waste_materials: 'High Waste Materials',
            high_waste_desc: 'Materials with significant waste requiring attention',
            waste_rate: 'waste rate',
            recommendations: 'Waste Reduction Recommendations'
          },
          usage: {
            material_consumption: 'Material Consumption',
            material_consumption_desc: 'Track usage patterns and forecast restock needs',
            used: 'Used',
            current_stock: 'Current',
            daily_avg: 'Daily Avg',
            days_left: 'days left',
            well_stocked: 'Well stocked',
            no_data: 'No Usage Data Available',
            no_data_desc: 'Start tracking materials and recording transactions to see usage analytics'
          },
          recipes: {
            cost_analysis: 'Recipe Cost Analysis',
            cost_analysis_desc: 'Analyze profitability and costs for each recipe',
            ingredients: 'ingredients',
            margin: 'margin',
            cost_per_serving: 'Cost/serving',
            selling_price: 'Price',
            yield: 'Yield',
            no_data: 'No Recipes Found',
            no_data_desc: 'Create recipes in the Recipes tab to analyze their costs and profitability'
          },
          insights: {
            healthy_gross_margin: 'Strong gross profit margin above 50%',
            good_stock_turnover: 'Healthy stock turnover above 4x per period',
            profitable_period: 'Net profit is positive this period',
            high_cogs: 'COGS ratio is high - consider recipe optimization',
            high_waste: 'Waste exceeds 5% - review inventory practices',
            many_low_stock: 'Multiple items low on stock - review reorder points'
          },
          key_insights: {
            title: 'Key Insights',
            positive_indicators: 'Positive Indicators',
            areas_for_improvement: 'Areas for Improvement'
          },
          transaction_types: {
            purchases: 'Purchases',
            sales: 'Sales',
            adjustments: 'Adjustments'
          },
          export_button: 'Export Data'
        },
        costs: {
          title: 'Cost Analysis',
          description: 'Detailed cost analysis and optimization insights',
          metrics: {
            total_inventory_value: 'Total Inventory Value',
            average_cost_per_item: 'Average Cost per Item',
            monthly_spending: 'Monthly Spending',
            cost_variance: 'Cost Variance'
          },
          analysis: {
            top_expensive_items: 'Top Expensive Items',
            cost_distribution: 'Cost Distribution',
            cost_trends: 'Cost Trends',
            cost_efficiency: 'Cost Efficiency'
          },
          charts: {
            cost_trends: 'Cost Trends (Last 6 Months)',
            supplier_analysis: 'Supplier Cost Analysis'
          },
          labels: {
            total_cost: 'Total Cost',
            avg_cost: 'Avg Cost',
            items: 'Items',
            avg: 'Avg'
          },
          no_data: 'No cost data available',
          cost_efficiency_insights: {
            title: 'Cost Efficiency Insights',
            cost_optimization: 'Cost Optimization',
            cost_concerns: 'Cost Concerns',
            low_cost_items: 'low-cost items (good value)',
            medium_cost_items: 'medium-cost items (balanced)',
            high_cost_items: 'high-cost items need review',
            monthly_spending: 'Monthly spending',
            cost_variance: 'Cost variance',
            average_cost_per_item: 'Average cost per item'
          }
        },
        advanced_analytics: {
          title: 'Advanced Analytics',
          description: 'Deep insights into menu costs, production efficiency, and business performance',
          metrics: {
            total_revenue: 'Total Revenue',
            total_profit: 'Total Profit',
            overall_efficiency: 'Overall Efficiency',
            staff_efficiency: 'Staff Efficiency'
          },
          tabs: {
            menu_costs: 'Menu Costs',
            production: 'Production',
            staff: 'Staff',
            materials: 'Materials',
            waste: 'Material Waste',
            variance: 'Cost Variance'
          },
          tables: {
            menu_item_cost_analysis: 'Menu Item Cost Analysis',
            production_efficiency_analysis: 'Production Efficiency Analysis',
            staff_performance_analysis: 'Staff Performance Analysis',
            material_usage_analysis: 'Material Usage Analysis',
            cost_variance_analysis: 'Cost Variance Analysis'
          },
          columns: {
            item: 'Item',
            base_price: 'Base Price',
            cost: 'Cost',
            profit_margin: 'Profit Margin',
            sales: 'Sales',
            revenue: 'Revenue',
            efficiency: 'Efficiency',
            expected: 'Expected',
            actual: 'Actual',
            waste: 'Waste',
            cost_variance: 'Cost Variance',
            staff_performance: 'Staff Performance',
            staff_member: 'Staff Member',
            items_produced: 'Items Produced',
            waste_percentage: 'Waste %',
            quality_score: 'Quality Score',
            material: 'Material',
            variance: 'Variance',
            cost_impact: 'Cost Impact',
            trend: 'Trend',
            recipe: 'Recipe',
            ingredients: 'Ingredients',
            labor: 'Labor',
            overhead: 'Overhead',
            total_cost: 'Total Cost',
            selling_price: 'Selling Price'
          },
          cost_variance_analysis: {
            title: 'Cost Variance Analysis',
            expected_cost: 'Expected Cost',
            actual_cost: 'Actual Cost',
            variance: 'Variance',
            top_variance_items: 'Top Variance Items',
            efficiency_insights: 'Efficiency Insights'
          },
          no_data: 'No analytics data available'
        },


        recipe_costs: {
          title: 'Recipe Cost Calculator',
          description: 'Analyze recipe costs, profit margins, and optimization opportunities',
          metrics: {
            total_recipes: 'Total Recipes',
            average_cost: 'Average Cost',
            average_profit_margin: 'Average Profit Margin',
            total_profit_potential: 'Total Profit Potential'
          },
          tables: {
            recipe_cost_analysis: 'Recipe Cost Analysis',
            top_profitable_recipes: 'Top Profitable Recipes',
            cost_optimization_opportunities: 'Cost Optimization Opportunities'
          },
          columns: {
            recipe: 'Recipe',
            ingredients: 'Ingredients',
            labor: 'Labor',
            overhead: 'Overhead',
            total_cost: 'Total Cost',
            selling_price: 'Selling Price',
            profit_margin: 'Profit Margin',
            efficiency: 'Efficiency',
            trend: 'Trend'
          },
          no_data: 'No recipe cost data available'
        },
        business_insights: {
          title: 'Business Insights Dashboard',
          description: 'Actionable insights to optimize your restaurant\'s performance and profitability',
          metrics: {
            overall_efficiency: 'Overall Efficiency',
            staff_efficiency: 'Staff Efficiency',
            items_needing_attention: 'Items Needing Attention',
            cost_savings_potential: 'Cost Savings Potential'
          },
          tables: {
            production_efficiency_analysis: 'Production Efficiency Analysis',
            material_usage_patterns: 'Material Usage Patterns & Weekly Comparison',
            cost_trends_analysis: 'Cost Trends Analysis'
          },
          columns: {
            menu_item: 'Menu Item',
            expected: 'Expected',
            actual: 'Actual',
            efficiency: 'Efficiency',
            waste: 'Waste',
            cost_variance: 'Cost Variance',
            trend: 'Trend',
            material: 'Material',
            variance: 'Variance',
            cost_impact: 'Cost Impact',
            this_week: 'This Week',
            last_week: 'Last Week',
            change: 'Change'
          },
          action_required: 'Action Required',
          cost_trend_analysis: 'Cost Trend Analysis',
          no_data: 'No business insights data available'
        },
        reports: {
          title: 'Reports',
          description: 'Detailed inventory reports and analysis',
          filters: {
            search: 'Search materials...',
            stock_status: 'Stock Status',
            all_statuses: 'All Statuses',
            in_stock: 'In Stock',
            low_stock: 'Low Stock',
            out_of_stock: 'Out of Stock',
            supplier: 'Supplier',
            all_suppliers: 'All Suppliers',
            date_range: 'Date Range',
            transaction_type: 'Transaction Type',
            all_types: 'All Types',
            purchase: 'Purchase',
            sale: 'Sale',
            adjustment: 'Adjustment'
          },
          summary: {
            materials_count: 'Materials Count',
            total_value: 'Total Value',
            low_stock_count: 'Low Stock',
            out_of_stock_count: 'Out of Stock'
          },
          tabs: {
            materials: 'Materials',
            transactions: 'Transactions'
          },
          columns: {
            material: 'Material',
            stock_level: 'Stock Level',
            status: 'Status',
            cost_per_unit: 'Cost/Unit',
            total_value: 'Total Value',
            supplier: 'Supplier',
            transactions: 'Transactions',
            last_activity: 'Last Activity',
            type: 'Type',
            quantity: 'Quantity',
            total_cost: 'Total Cost',
            date: 'Date'
          },
          export_button: 'Export to CSV',
          no_data: 'No report data available'
        },

        materials: {
          title: 'Materials',
          description: 'Manage raw materials and track stock levels',
          add_material: 'Add Material',
          edit_material: 'Edit Material',
          delete_material: 'Delete Material',
          material_name: 'Material Name',
          material_name_placeholder: 'Enter material name',
          material_description: 'Description',
          material_description_placeholder: 'Enter material description',
          category: 'Category',
          category_placeholder: 'Select category',
          unit: 'Unit',
          unit_placeholder: 'Select unit',
          current_stock: 'Current Stock',
          current_stock_placeholder: 'Enter current stock',
          minimum_stock: 'Minimum Stock',
          minimum_stock_placeholder: 'Enter minimum stock level',
          cost_per_unit: 'Cost per Unit',
          cost_per_unit_placeholder: 'Enter cost per unit',
          supplier: 'Supplier',
          supplier_placeholder: 'Enter supplier name',
          location: 'Location',
          location_placeholder: 'Enter storage location',
          save_button: 'Save Material',
          saving_button: 'Saving...',
          cancel_button: 'Cancel',
          delete_confirm_title: 'Delete Material',
          delete_confirm_desc: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
          toasts: {
            success_title: 'Success',
            success_desc: 'Material saved successfully',
            error_title: 'Error',
            error_desc: 'Failed to save material',
            delete_success_title: 'Success',
            delete_success_desc: 'Material deleted successfully',
            delete_error_title: 'Error',
            delete_error_desc: 'Failed to delete material'
          }
        },
        recipes: {
          title: 'Recipes',
          description: 'Manage recipes and ingredient requirements',
          add_recipe: 'Add Recipe',
          edit_recipe: 'Edit Recipe',
          delete_recipe: 'Delete Recipe',
          recipe_name: 'Recipe Name',
          recipe_name_placeholder: 'Enter recipe name',
          recipe_description: 'Description',
          recipe_description_placeholder: 'Enter recipe description',
          ingredients: 'Ingredients',
          add_ingredient: 'Add Ingredient',
          ingredient: 'Ingredient',
          ingredient_placeholder: 'Select ingredient',
          quantity: 'Quantity',
          quantity_placeholder: 'Enter quantity',
          unit: 'Unit',
          unit_placeholder: 'Select unit',
          save_button: 'Save Recipe',
          saving_button: 'Saving...',
          cancel_button: 'Cancel',
          delete_confirm_title: 'Delete Recipe',
          delete_confirm_desc: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
          toasts: {
            success_title: 'Success',
            success_desc: 'Recipe saved successfully',
            error_title: 'Error',
            error_desc: 'Failed to save recipe',
            delete_success_title: 'Success',
            delete_success_desc: 'Recipe deleted successfully',
            delete_error_title: 'Error',
            delete_error_desc: 'Failed to delete recipe'
          }
        },
        settings: {
          title: 'Settings',
          description: 'Configure inventory management settings',
          general_settings: 'General Settings',
          notifications: 'Notifications',
          low_stock_alert: 'Low Stock Alert',
          low_stock_alert_desc: 'Get notified when items reach minimum stock level',
          out_of_stock_alert: 'Out of Stock Alert',
          out_of_stock_alert_desc: 'Get notified when items are out of stock',
          cost_tracking: 'Cost Tracking',
          cost_tracking_desc: 'Track costs and calculate profit margins',
          save_button: 'Save Settings',
          saving_button: 'Saving...',
          toasts: {
            success_title: 'Success',
            success_desc: 'Settings saved successfully',
            error_title: 'Error',
            error_desc: 'Failed to save settings'
          }
        }
      },
      menu_management: {
        title: 'Menu Management',
        description: 'Organize your menu items and categories for each branch.',
        new_category_button: 'New Category',
        select_branch_prompt_title: 'Select a Branch to Begin',
        select_branch_prompt_desc: 'Choose a branch from the dropdown above to manage its menu.',
        no_categories_title: 'No Categories Yet',
        no_categories_desc: 'Create your first category or upload a menu to get started.',
        create_first_category_button: 'Create First Category',
        active_badge: 'Active',
        inactive_badge: 'Inactive',
        add_item_button: 'Add Item',
        open_menu: 'Open menu',
        edit_category_button: 'Edit Category',
        delete_category_button: 'Delete Category',
        edit_item_button: 'Edit Item',
        delete_item_button: 'Delete Item',
        available_label: 'Available',
        availability_enabled: 'enabled successfully',
        availability_disabled: 'disabled successfully',
        availability_update_error: 'Failed to update item availability',
        delete_category_title: 'Delete Category',
        delete_category_desc: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        delete_menu_item_title: 'Delete Menu Item',
        delete_menu_item_desc: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        toasts: {
          update_order_error_title: 'Error',
          update_order_error_desc: 'Could not save the new order. Please try again.',
          update_order_success_title: 'Success!',
          update_order_success_desc: 'New order saved.',
          delete_success_title: 'Success!',
          delete_success_desc: '{{type}} has been deleted.',
          delete_error_title: 'Error',
          delete_error_desc: 'Deletion failed. Please try again.',
          reorder_success_title: 'Success!',
          reorder_success_desc: 'Items reordered successfully.',
          reorder_error_title: 'Error',
          reorder_error_desc: 'Failed to reorder items. Please try again.'
        },
        menu_upload_component: {
          title: "Upload Your Menu",
          description: "Upload an image or PDF of your existing menu, and we'll automatically extract the categories, items, and prices to build your digital menu.",
          uploading_and_processing: 'Uploading & Processing...',
          drop_here: 'Drop your menu file here',
          click_to_upload: 'Click to upload',
          drag_and_drop: 'or drag and drop',
          supported_files: 'JPG, PNG, WebP images & PDF supported',
          recent_uploads_title: 'Recent Uploads',
          recent_uploads_description: 'View and manage your menu upload history',
          status: {
            processing: 'Processing',
            completed: 'Completed',
            failed: 'Failed',
            unknown: 'Unknown'
          },
          preview_button: 'Preview',
          view_file_button: 'View',
          delete_dialog_title: 'Delete Menu Upload',
          delete_dialog_description: 'Are you sure you want to delete "{{fileName}}? This action cannot be undone.',
          cancel_button: 'Cancel',
          delete_button: 'Delete',
          delete_error_toast: 'Failed to delete upload. Please try again.'
        },
        complete_setup_to_see_activity: 'Complete setup to see activity'
      },
      menu_item: {
        available: 'Available',
        unavailable: 'Unavailable',
        choose_size: 'Choose size',
        select_variant_first: 'Select a size first',
        regular: 'Regular',
        add_to_cart: 'Add to Cart'
      },
      rating: {
        rate_this_item: 'Rate this item',
        loading: 'Loading...',
        no_ratings_yet: 'No ratings yet',
        no_reviews_yet: 'No reviews yet',
        rating_required: 'Rating Required',
        please_select_rating: 'Please select a rating before submitting.',
        your_rating: 'Your Rating',
        your_name_optional: 'Your Name (Optional)',
        comment_optional: 'Comment (Optional)',
        submit_rating: 'Submit Rating',
        submitting: 'Submitting...',
        thank_you: 'Thank you!',
        rating_submitted_success: 'Your rating has been submitted successfully.',
        error: 'Error',
        submit_failed: 'Failed to submit rating. Please try again.',
        already_rated: 'Already Rated',
        already_rated_message: 'You have already rated this item',
        rate_item: 'Rate {{itemName}}',
        rating_dialog_description: 'Share your experience with this item to help other customers.',
        name_placeholder: 'Enter your name',
        comment_placeholder: 'Tell us about your experience with this item...',
        review_singular: 'review',
        reviews_plural: 'reviews'
      },
      pop_offer: {
        title: "Pop-up Offer",
        description: "Create or manage a promotional pop-up for your menu.",
        status: {
          no_offer: "No Offer",
          expired: "Expired",
          scheduled: "Scheduled",
          active: "Active",
        },
        image_label: "Offer Image",
        description_label: "Description",
        description_placeholder: "e.g., 'Get 20% off on all pizzas!'",
        start_time_label: "Start Time",
        end_time_label: "End Time",
        save_button: "Save Offer",
        saving_button: "Saving...",
        delete_button: "Delete Offer",
        deleting_button: "Deleting...",
        view_history_button: "View Offer History",
        archived_offers_title: "Archived Offers",
        archived_offers_description: "A history of all past promotional offers for this branch.",
        loading_history: "Loading history...",
        no_archived_offers: "No archived offers found.",
        ran_from: "Ran from",
        to: "to",
        archived_on: "Archived on",
        toasts: {
          save_success_title: "Success",
          save_success_description: "Promotional offer saved successfully.",
          save_error_title: "Error",
          save_error_description: "Failed to save promotional offer.",
          delete_success_title: "Success",
          delete_success_description: "Promotional offer deleted successfully.",
          delete_error_title: "Error",
          delete_error_description: "Failed to delete promotional offer."
        },
        delete_confirm: "Are you sure you want to delete this offer?"
      },
      menu: {
        title: 'Digital Menu',
        unavailable: 'Unavailable',
        item_unavailable_message: 'This item is currently unavailable'
      },
      menuManagement: {
        uploadSuccess: 'Upload Successful',
        pdfUploadedAsReference: 'PDF uploaded as reference. AI extraction is only available for images.',
        processingMenu: 'Your menu is being processed. This may take a few moments.'
      },
      orders: {
        title: "Orders",
        description: "View and manage incoming orders from your customers.",
        my_orders: "My Orders",
        orders: "Orders",
        no_orders: "No orders yet",
        error_loading: "Error loading orders",
        items: "Items",
        order_number: "Order #",
        status: "Status",
        date: "Date",
        total: "Total",
        status_labels: {
          pending: 'Pending',
          confirmed: 'Confirmed',
          preparing: 'Preparing',
          ready: 'Ready',
          completed: 'Completed',
          cancelled: 'Cancelled'
        },
        delivery_status: {
          assigned: 'Assigned',
          accepted: 'Accepted',
          picked_up: 'Picked Up',
          in_transit: 'In Transit',
          delivered: 'Delivered',
          cancelled: 'Cancelled'
        },
      },
      tables: {
        title: 'Table Management',
        description: 'Configure and manage dine-in tables for your restaurant',
        add_table: 'Add Table',
        edit_table: 'Edit Table',
        delete_table: 'Delete Table',
        table_number: 'Table Number',
        table_name: 'Table Name (Optional)',
        capacity: 'Capacity',
        section: 'Section',
        is_active: 'Active',
        notes: 'Notes',
        qr_code: 'QR Code',
        generate_qr: 'Generate QR Code',
        download_qr: 'Download QR',
        no_tables: 'No tables configured yet',
        no_tables_hint: 'Add tables to enable dine-in ordering with QR codes',
        select_branch_hint: 'Please select a branch to manage tables',
        qr_for_table: 'QR Code for Table {{table}}',
        qr_dialog_description: 'Scan this QR code to open the menu with this table pre-selected',
        qr_downloaded: 'QR Code Downloaded',
        qr_downloaded_desc: 'The QR code has been saved to your device',
        url_copied: 'URL Copied',
        copy_url: 'Copy URL',
        open_menu: 'Open Menu',
        sections: {
          indoor: 'Indoor',
          outdoor: 'Outdoor',
          bar: 'Bar',
          vip: 'VIP',
          patio: 'Patio',
          private: 'Private Room'
        },
        form: {
          table_number_placeholder: 'e.g., 1, A1, T-05',
          table_name_placeholder: 'e.g., Window Seat, Booth 3',
          capacity_placeholder: 'Number of seats',
          section_placeholder: 'Select section',
          notes_placeholder: 'Additional notes about this table...'
        },
        messages: {
          created: 'Table created successfully',
          updated: 'Table updated successfully',
          deleted: 'Table deleted successfully',
          qr_generated: 'QR code generated successfully'
        },
        errors: {
          duplicate: 'A table with this number already exists',
          limit_reached: 'Table limit reached. Upgrade your plan to add more tables.',
          not_found: 'Table not found',
          create_failed: 'Failed to create table',
          update_failed: 'Failed to update table',
          delete_failed: 'Failed to delete table',
          qr_download_failed: 'Failed to download QR code'
        },
        confirm_delete: 'Are you sure you want to delete this table?',
        limit_info: '{{current}} of {{limit}} tables used',
        upgrade_for_tables: 'Upgrade to Pro or Enterprise to enable table management'
      },
      audit: {
        title: 'Audit Logs',
        description: 'Track all changes and activities in your restaurant',
        upgrade_required: 'Audit Logs - Premium Feature',
        upgrade_description: 'Upgrade to Enterprise plan to access detailed audit logs and activity tracking.',
        no_logs: 'No Audit Logs Found',
        no_logs_description: 'No activity has been recorded yet or no logs match your filters.',
        search_placeholder: 'Search by name or entity...',
        all_actions: 'All Actions',
        all_entities: 'All Entities',
        all_branches: 'All Branches',
        action: 'Action',
        entity_type: 'Entity Type',
        entity: 'Entity',
        entity_name: 'Entity Name',
        entity_id: 'Entity ID',
        user: 'User',
        timestamp: 'Timestamp',
        start_date: 'Start Date',
        end_date: 'End Date',
        log_details: 'Audit Log Details',
        changes: 'Changes',
        metadata: 'Additional Details',
        technical_info: 'Technical Information',
        showing: 'Showing',
        actions: {
          create: 'Create',
          update: 'Update',
          delete: 'Delete',
          login: 'Login',
          logout: 'Logout',
          permission_change: 'Permission Change',
          status_change: 'Status Change',
          export: 'Export',
          import: 'Import',
          other: 'Other'
        },
        entities: {
          order: 'Order',
          menu_item: 'Menu Item',
          category: 'Category',
          branch: 'Branch',
          staff: 'Staff',
          table: 'Table',
          inventory: 'Inventory',
          material: 'Material',
          recipe: 'Recipe',
          settings: 'Settings',
          subscription: 'Subscription',
          payment: 'Payment',
          customer: 'Customer',
          reservation: 'Reservation',
          authentication: 'Authentication',
          transaction: 'Transaction',
          expense: 'Expense',
          account: 'Account'
        }
      },
      dialogs: {
        edit_menu_item: {
          title: 'Edit Menu Item',
          description: 'Update the details for this menu item.',
          name_label: 'Name',
          name_placeholder: 'Enter item name',
          description_label: 'Description',
          description_placeholder: 'Enter item description',
          price_label: 'Price',
          price_placeholder: 'Enter price',
          image_label: 'Image',
          upload_prompt: 'Click to upload or drag and drop',
          upload_formats: 'JPG, PNG, or WebP (max 10MB)',
          drop_image_prompt: 'Drop image here',
          uploading: 'Uploading...',
          featured_label: 'Featured',
          available_label: 'Available',
          cancel_button: 'Cancel',
          update_button: 'Update',
          updating_button: 'Updating...',
          toasts: {
            image_upload_success_title: 'Image Uploaded',
            image_upload_success_desc: 'The image was uploaded successfully.',
            image_upload_error_title: 'Upload Error',
            image_upload_error_desc: 'Failed to upload image.',
            update_success_title: 'Success',
            update_success_desc: 'Menu item updated successfully.',
            update_error_title: 'Error',
            update_error_desc: 'Failed to update menu item.'
          }
        },
        create_menu_item: {
          title: 'Create New Menu Item',
          description: 'Add a new item to your menu category.',
          name_label: 'Item Name *',
          name_placeholder: 'e.g., Margherita Pizza',
          description_label: 'Description',
          description_placeholder: 'Describe ingredients and preparation...',
          price_label: 'Base Price *',
          price_placeholder: '19.99',
          image_label: 'Item Image',
          image_upload_button: 'Click to upload image',
          featured_label: 'Featured Item',
          available_label: 'Available',
          cancel_button: 'Cancel',
          create_button: 'Create Menu Item',
          creating_button: 'Creating...',
          toasts: {
            image_upload_success_title: 'Success!',
            image_upload_success_desc: 'Image uploaded successfully.',
            image_upload_error_title: 'Error',
            image_upload_error_desc: 'Failed to upload image.',
            create_success_title: 'Success!',
            create_success_desc: 'Menu item created successfully.',
            create_error_title: 'Error',
            create_error_desc: 'Failed to create menu item. Please try again.'
          },
          variants: {
            title: 'Variants (Optional)',
            subtitle: 'Add different sizes or options with different prices and descriptions.',
            size_placeholder: 'Variant size (e.g. Small, Medium, Large)',
            price_placeholder: 'Price',
            description_placeholder: 'Variant description (optional) - e.g., nutritional info, calories...',
            add_button: 'Add Variant',
            save_button: 'Save',
            cancel_button: 'Cancel',
            no_variants: 'No variants added yet.',
            edit: 'Edit',
            delete: 'Delete'
          }
        },
        create_category: {
          title: 'Create New Category',
          description: 'Add a new category to organize your menu items.',
          name_label: 'Category Name *',
          name_placeholder: 'e.g., Appetizers, Main Dishes',
          description_label: 'Description',
          description_placeholder: 'A short description for this category...',
          image_label: 'Category Image',
          image_upload_button: 'Click to upload image',
          cancel_button: 'Cancel',
          create_button: 'Create Category',
          creating_button: 'Creating...',
          toasts: {
            image_upload_success_title: 'Success!',
            image_upload_success_desc: 'Image uploaded successfully.',
            image_upload_error_title: 'Error',
            image_upload_error_desc: 'Failed to upload image.',
            create_success_title: 'Success!',
            create_success_desc: 'Category created successfully.',
            create_error_title: 'Error',
            create_error_desc: 'Failed to create category. Please try again.'
          }
        },
        edit_category: {
          title: 'Edit Category',
          description: 'Update your menu category details.',
          name_label: 'Category Name *',
          name_placeholder: 'e.g., Appetizers, Main Dishes',
          description_label: 'Description',
          description_placeholder: 'A short description for this category...',
          image_label: 'Category Image',
          image_upload_button: 'Click to upload image',
          active_label: 'Category is Active',
          cancel_button: 'Cancel',
          update_button: 'Update Category',
          updating_button: 'Updating...',
          toasts: {
            image_upload_success_title: 'Success!',
            image_upload_success_desc: 'Image uploaded successfully.',
            image_upload_error_title: 'Error',
            image_upload_error_desc: 'Failed to upload image.',
            update_success_title: 'Success!',
            update_success_desc: 'Category updated successfully.',
            update_error_title: 'Error',
            update_error_desc: 'Failed to update category. Please try again.'
          }
        },
        delete_confirm: {
          title: 'Confirm Deletion',
          description: 'Are you sure you want to delete this item? This action cannot be undone.',
          cancel_button: 'Cancel',
          delete_button: 'Delete'
        }
      }
    },
    cart: {
      items: "items",
      title: "Your Order",
      subtotal: "Subtotal",
      loyalty_discount: "Loyalty Discount",
      loading_zones: "Loading zones...",
      minimum_order: "Minimum Order",
      suggestions_title: "These items pair well with your current order.",
      add_to_order: "Add to Order",
      total: "Total",
      contact_info: "Contact Information",
      order_type: "Order Type",
      pickup: "Pickup",
      delivery: "Delivery",
      dine_in: "Dine In",
      select_order_type: "How would you like to receive your order?",
      name_label: "Name",
      name_optional: "Name (Optional)",
      name_placeholder: "Enter your full name",
      phone_label: "Phone",
      phone_optional: "Phone (Optional)",
      phone_placeholder: "Enter your phone number",
      email_label: "Email",
      email_placeholder: "Your email address",
      address_label: "Delivery Address",
      address_placeholder: "Enter your detailed address (Street name, Building No, Apt No)",
      table_number_label: "Table Number",
      table_number_placeholder: "Enter your table number",
      delivery_zone_label: "Delivery Zone",
      delivery_zone_placeholder: "Select your delivery zone",
      delivery_fee: "Delivery Fee",
      estimated_delivery: "Estimated Delivery",
      estimated_pickup: "Estimated Pickup",
      minutes: "minutes",
      notes_label: "Special Notes",
      notes_optional: "Notes (Optional)",
      notes_placeholder: "Any special notes or additional requests",
      auto_selected_qr: "Order type automatically selected from QR code",
      table_prefix: "Table",
      send_order: "Send Order",
      order_success_title: "Order Sent!",
      order_success_desc: "Your order has been received.",
      order_error_title: "Error",
      order_error_desc: "Failed to send order. Please try again.",
      missing_info_title: "Missing Information",
      missing_info_desc: "Please provide your name and phone number.",
      missing_table_number: "Please provide your table number for dine-in orders.",
      missing_delivery_address: "Please provide your delivery address.",
      missing_delivery_zone: "Please select a delivery zone for delivery orders.",
      delivery_minimum_not_met: "Order total doesn't meet the minimum for this delivery zone.",
      empty_cart_title: "Cart is Empty",
      empty_cart_desc: "Please add items to your cart before ordering.",
      no_delivery_zones: "No delivery zones available for this location.",
      order_limit_reached_title: "Order Limit Reached",
      order_limit_reached_desc: "This restaurant has reached its monthly order limit. Please contact the restaurant for assistance."
    },
    staff_order_manager: {
      title: "Staff Order Manager",
      description: "Create and manage orders manually for in-store customers",
      menu_items: "Menu Items",
      search_placeholder: "Search menu items...",
      current_order: "Current Order",
      order_type: "Order Type",
      customer_info: "Customer Information",
      payment_method: "Payment Method",
      order_summary: "Order Summary",
      create_order: "Create Order",
      clear_cart: "Clear Cart",
      add_to_order: "Add to Order"
    },
    loyalty_program: {
      title: "Loyalty Program",
      description: "Build customer loyalty with points-based rewards",
      enable_program: "Enable Loyalty Program",
      program_name: "Program Name",
      points_per_dollar: "Points per Dollar",
      min_redeem: "Minimum Points to Redeem",
      point_value: "Point Value",
      total_members: "Total Members",
      active_members: "Active Members",
      points_earned: "Points Earned",
      points_redeemed: "Points Redeemed"
    },
    onboarding: {
      welcome_title: "Welcome to Ripplefy!",
      welcome_description: "Your complete restaurant management solution",
      step_restaurant_setup: "Set Up Your Restaurant",
      step_branches: "Add Your Locations",
      step_menu: "Create Your Digital Menu",
      step_qr_codes: "Generate QR Codes",
      step_orders: "Test Order System",
      step_customization: "Customize Your Brand",
      step_staff: "Add Staff Members",
      step_analytics: "Explore Analytics",
      congratulations: "You're All Set!",
      next: "Next",
      previous: "Previous",
      skip: "Skip Step",
      complete: "Complete"
    },
    kitchen_display: {
      title: "Kitchen Display",
      description: "Real-time order management for kitchen staff",
      active_orders: "Active Orders",
      total_items: "Total Items",
      ready_items: "Ready Items",
      avg_prep_time: "Avg Prep Time",
      all_stations: "All Stations",
      start_cooking: "Start",
      mark_ready: "Ready",
      complete_order: "Complete Order",
      urgent: "URGENT",
      high: "HIGH",
      normal: "NORMAL"
    },
    advanced_analytics: {
      title: "Advanced Analytics",
      description: "Comprehensive insights into restaurant performance",
      total_revenue: "Total Revenue",
      total_orders: "Total Orders",
      avg_order_value: "Average Order Value",
      total_customers: "Total Customers",
      overview: "Overview",
      sales: "Sales",
      customers: "Customers",
      menu_performance: "Menu Performance",
      operations: "Operations",
      revenue_trend: "Revenue Trend",
      order_types: "Order Types",
      peak_hours: "Peak Hours Analysis",
      customer_insights: "Customer Insights",
      top_customers: "Top Customers",
      pickup: "Pickup",
      delivery: "Delivery",
      dine_in: "Dine-in",
      customer_retention_rate: "Customer Retention Rate",
      new_customer_rate: "New Customer Rate",
      avg_order_frequency: "Average Order Frequency",
      customer_lifetime_value: "Customer Lifetime Value",
      export_started: "Export Started",
      exporting_as: "Exporting analytics data as {{format}}...",
      no_analytics_data: "No Analytics Data",
      no_orders_message: "{{branch}} has no orders in the selected time period. Start receiving orders to see analytics and insights.",
      refresh: "Refresh",
      export: "Export",
      select_branch: "Select Branch",
      vs_previous_period: "vs previous period",
      vs_last_period: "vs last period",
      orders: "orders",
      new_customers: "New Customers",
      returning_customers: "Returning Customers",
      customer_segmentation: "Customer Segmentation",
      top_performing_items: "Top Performing Items",
      top_items_description: "Most popular menu items based on sales volume and revenue for {{branch}}",
      loading_menu_performance: "Loading menu performance data...",
      no_menu_data: "No menu performance data available yet",
      no_menu_data_desc: "Data will appear after customers place orders with menu items.",
      menu_data_tips: "• Make sure you have menu items added to your menu\n• Ensure orders are being placed with menu items\n• Check that orders are not being cancelled",
      sold: "sold",
      revenue: "Revenue",
      avg: "Avg",
      items_sold: "Items Sold",
      total_units: "total units",
      no_sales_yet: "No sales yet",
      average_price: "Average Price",
      per_item: "per item",
      popular_items: "Popular Items",
      no_revenue_yet: "No revenue yet",
      menu_performance_insights: "Menu Performance Insights",
      top_revenue_generator: "Top Revenue Generator",
      of_total_menu_revenue: "of total menu revenue",
      most_sold_item: "Most Sold Item",
      units: "units",
      of_total_units_sold: "of total units sold",
      operational_metrics: "Operational Metrics",
      order_fulfillment_rate: "Order Fulfillment Rate",
      avg_prep_time: "Average Prep Time",
      active_branches: "Active Branches",
      branch_performance: "Branch Performance",
      no_branches_configured: "No branches configured",
      today: "Today",
      days_7: "7 Days",
      days_30: "30 Days",
      days_90: "90 Days",
      year_1: "1 Year",
      total_revenue_label: "total revenue",
      total_orders_summary: "Total Orders",
      avg_order_summary: "Avg Order Value",
      orders_label: "orders",
      avg_short: "Avg",
      min: "min",
      top_performing_items_desc: "Most popular menu items based on sales volume and revenue for {{branchName}}",
      menu_tip_1: "Make sure you have menu items added to your menu",
      menu_tip_2: "Ensure orders are being placed with menu items",
      menu_tip_3: "Check that orders are not being cancelled"
    },
    staff_management: {
      title: 'Staff Management',
      subtitle: 'Manage your team members and their access levels',
      create_staff: 'Create New Staff',
      staff_list: 'Staff Members',
      form: {
        username: 'Username',
        username_placeholder: 'Enter username',
        password: 'Password',
        password_placeholder: 'Enter password',
        role: 'Role',
        role_placeholder: 'Select role',
        submit: 'Create Staff',
        cancel: 'Cancel'
      },
      roles: {
        OWNER: 'Owner',
        MANAGER: 'Manager',
        CASHIER: 'Cashier',
        VIEWER: 'Viewer'
      },
      messages: {
        create_success: 'Staff member created successfully',
        create_error: 'Failed to create staff member',
        load_error: 'Failed to load staff members',
        access_denied: 'Access denied. Only owners can manage staff.',
        no_restaurant: 'Restaurant not found.'
      },
      login: {
        title: 'Staff Login',
        username: 'Username',
        password: 'Password',
        login_button: 'Login as Staff',
        switch_to_owner: 'Login as Owner',
        error: 'Invalid username or password'
      }
    },
    rider: {
      loading: 'Loading...',
      dashboard_title: 'Rider Dashboard',
      welcome_back: 'Welcome back, {{name}}!',
      logout: 'Logout',
      customer: 'Customer',
      order_number: 'Order #{{number}}',
      assigned_at: 'Assigned',
      delivery_address: 'Delivery Address',
      order_total: 'Order Total',
      estimated_time: 'Estimated Time',
      minutes: 'minutes',
      your_earning: 'Your Earning',
      special_instructions: 'Special Instructions',
      min: 'min',
      no_active_deliveries: 'No Active Deliveries',
      waiting_for_deliveries: 'New deliveries will appear here when assigned',
      set_status_available: 'Set your status to "Available" to receive deliveries',
      no_delivery_history: 'No Delivery History',
      completed_deliveries_appear: 'Completed deliveries will appear here',
      today_performance: "Today's Performance",
      deliveries_completed: 'Deliveries Completed',
      total_earnings: 'Total Earnings',
      avg_delivery_time: 'Average Delivery Time',
      alltime_statistics: 'All-Time Statistics',
      total_deliveries: 'Total Deliveries',
      success_rate: 'Success Rate',
      rider_status: {
        available: 'Available',
        busy: 'Busy',
        on_break: 'On Break',
        offline: 'Offline'
      },
      status: {
        assigned: 'Assigned',
        accepted: 'Accepted',
        picked_up: 'Picked Up',
        in_transit: 'In Transit',
        delivered: 'Delivered',
        failed: 'Failed',
        cancelled: 'Cancelled'
      },
      stats: {
        active_deliveries: 'Active Deliveries',
        today_deliveries: "Today's Deliveries",
        today_earnings: "Today's Earnings",
        average_rating: 'Average Rating'
      },
      tabs: {
        active_deliveries: 'Active Deliveries',
        history: 'History',
        earnings_stats: 'Earnings & Stats'
      },
      actions: {
        accept_order: 'Accept Order',
        decline_order: 'Decline Order',
        mark_picked_up: 'Mark as Picked Up',
        out_for_delivery: 'Out for Delivery',
        mark_delivered: 'Mark as Delivered'
      },
      confirm_decline: 'Confirm Decline',
      decline_confirmation_message: 'Are you sure you want to decline this order? This action cannot be undone.',
      decline_button: 'Decline',
      confirm_decline_button: 'Yes, Decline Order',
      cancel: 'Cancel'
    },
    subscription: {
      upgrade_required: 'Upgrade Required',
      feature_not_available: 'This feature is not available on your current plan',
      upgrade_to_access: 'Upgrade your plan to access this feature',
      upgrade_to_continue: 'Please upgrade your subscription to continue.',
      limit_reached: 'Limit Reached',
      order_limit_reached: 'Monthly Order Limit Reached',
      order_limit_message: 'You have used {{current}} of {{limit}} orders this month. Please upgrade to continue processing orders.',
      order_limit_warning: 'You are approaching your monthly order limit ({{current}}/{{limit}}). Consider upgrading soon.',
      branches_limit_message: 'You have used {{current}} of {{limit}} branches. Please upgrade to add more branches.',
      branches_limit_warning: 'You are approaching your branch limit ({{current}}/{{limit}}). Consider upgrading soon.',
      staff_limit_message: 'You have used {{current}} of {{limit}} staff members. Please upgrade to add more staff.',
      staff_limit_warning: 'You are approaching your staff limit ({{current}}/{{limit}}). Consider upgrading soon.',
      menu_items_limit_message: 'You have used {{current}} of {{limit}} menu items. Please upgrade to add more items.',
      menu_items_limit_warning: 'You are approaching your menu items limit ({{current}}/{{limit}}). Consider upgrading soon.',
      categories_limit_message: 'You have used {{current}} of {{limit}} categories. Please upgrade to add more categories.',
      categories_limit_warning: 'You are approaching your categories limit ({{current}}/{{limit}}). Consider upgrading soon.',
      export_upgrade: 'Data export is available on Pro and Enterprise plans. Upgrade to export your data.',
      limit_reached_details: {
        title: 'Limit Reached',
        branches: 'You have reached your branch limit. Upgrade to add more branches.',
        staff: 'You have reached your staff limit. Upgrade to add more staff members.',
        menu_items: 'You have reached your menu item limit. Upgrade to add more items.',
        categories: 'You have reached your category limit. Upgrade to add more categories.',
        orders: 'You have reached your monthly order limit. Please upgrade your plan.',
        storage: 'You have reached your storage limit. Upgrade for more storage space.',
        tables: 'You have reached your table limit. Upgrade to add more tables.'
      },
      tier: {
        none: 'Not Available',
        basic: 'Basic',
        advanced: 'Advanced',
        enterprise: 'Enterprise',
        required: 'Required Tier: {{tier}}'
      },
      features_by_tier: {
        inventory_basic: 'View & update stock levels',
        inventory_advanced: 'Recipes, Analytics, Waste Tracking, Physical Count',
        analytics_basic: 'Key metrics overview',
        analytics_advanced: 'Charts, Trends, Custom Reports',
        marketing_basic: 'Promotional offers & discounts',
        marketing_advanced: 'Campaigns, Customer Insights',
        loyalty_basic: 'Points configuration, Customer list',
        loyalty_advanced: 'Analytics, Custom Rewards'
      },
      actions: {
        upgrade_now: 'Upgrade Now',
        view_plans: 'View Plans',
        contact_sales: 'Contact Sales',
        manage_subscription: 'Manage Subscription'
      },
      status: {
        active: 'Active',
        trialing: 'Trial',
        past_due: 'Past Due',
        canceled: 'Canceled',
        expired: 'Expired'
      },
      trial: {
        days_remaining: '{{days}} days remaining in your trial',
        expired: 'Your trial has expired',
        upgrade_prompt: 'Upgrade to continue using all features'
      },
      banner: {
        upgrade_title: 'Unlock More Features',
        upgrade_description: 'Upgrade your plan to access advanced features and grow your business.',
        limit_warning: 'You are approaching your {{resource}} limit ({{current}}/{{max}})'
      },
      usage_overview: 'Usage Overview',
      usage_description: 'Track your resource usage against your plan limits',
      resources: {
        branches: 'Branches',
        branches_desc: 'Restaurant locations',
        staff: 'Staff Members',
        staff_desc: 'Employee accounts',
        menu_items: 'Menu Items',
        menu_items_desc: 'Total menu items',
        categories: 'Categories',
        categories_desc: 'Menu categories',
        monthly_orders: 'Monthly Orders',
        monthly_orders_desc: 'Orders this month',
        storage: 'Storage',
        storage_desc: 'files uploaded',
        storage_gb: 'GB used'
      }
    },
    kds: {
      title: 'Kitchen Display',
      active_orders: 'active orders',
      no_orders: 'No active orders',
      waiting: 'Waiting for new orders...',
      columns: {
        new: 'New Orders',
        preparing: 'Preparing',
        ready: 'Ready'
      },
      status: {
        pending: 'New',
        confirmed: 'Confirmed',
        preparing: 'Preparing',
        ready: 'Ready',
        completed: 'Completed'
      },
      actions: {
        confirm: 'Confirm Order',
        start_preparing: 'Start Preparing',
        mark_ready: 'Mark Ready',
        complete: 'Complete & Clear'
      },
      messages: {
        status_updated: 'Order status updated'
      },
      errors: {
        fetch_failed: 'Failed to load orders',
        update_failed: 'Failed to update order',
        no_branch: 'Please select a branch'
      }
    },
    finance: {
      overview: 'Overview',
      profit_loss: 'Profit & Loss',
      cash_flow: 'Cash Flow',
      balance_sheet: 'Balance Sheet',
      expenses: 'Expenses',
      upgrade_required_title: 'Advanced Financial Reports',
      upgrade_required_desc: 'Upgrade to Pro or Enterprise to access detailed financial reports including Profit & Loss statements, expense tracking, cash flow analysis, and balance sheets.',
      basic_feature_hint: 'With your current plan, you can view Daily Sales Reports in the Orders page.',
      enterprise_feature: 'Enterprise Feature',
      cash_flow_upgrade: 'Cash Flow analysis is available on the Enterprise plan. Upgrade to track detailed cash movements.',
      balance_sheet_upgrade: 'Balance Sheet and Financial Accounts management is available on the Enterprise plan. Upgrade to track assets, liabilities, and equity.'
    },
    marketing: {
      upgradeTitle: 'Unlock Advanced Marketing',
      upgradeDescription: 'Upgrade to access SMS/WhatsApp campaigns, customer insights, and detailed analytics',
      promotionalOffers: 'Promotional Offers',
      campaigns: 'Campaigns',
      customerInsights: 'Customer Insights',
      createOffer: 'Create Offer',
      createCampaign: 'Create Campaign',
      noOffers: 'No promotional offers created yet',
      noOffersHint: 'Create your first promotional offer to engage customers',
      noCampaigns: 'No campaigns created yet',
      noCampaignsHint: 'Create your first marketing campaign to reach customers',
      offerName: 'Offer Name',
      promoCode: 'Promo Code',
      discountType: 'Discount Type',
      percentageDiscount: 'Percentage Discount',
      fixedDiscount: 'Fixed Amount Discount',
      minOrder: 'Minimum Order',
      usageLimit: 'Usage Limit',
      description: 'Description',
      campaignName: 'Campaign Name',
      campaignType: 'Campaign Type',
      targetAudience: 'Target Audience',
      allCustomers: 'All Customers',
      newCustomers: 'New Customers',
      returningCustomers: 'Returning Customers',
      inactiveCustomers: 'Inactive Customers',
      vipCustomers: 'VIP Customers',
      messageTemplate: 'Message Template',
      start: 'Start',
      totalCampaigns: 'Total Campaigns',
      messagesSent: 'Messages Sent',
      deliveryRate: 'Delivery Rate',
      marketingCost: 'Marketing Cost',
      topCustomers: 'Top Customers',
      orders: 'orders',
      avgOrder: 'avg',
      lowRisk: 'Low Risk',
      mediumRisk: 'Medium Risk',
      highRisk: 'High Risk'
    },
    reservations: {
      title: 'Reservations',
      subtitle: 'Manage table bookings and reservations',
      upgradeDescription: 'Manage table bookings and reservations for your restaurant',
      upgradePrompt: 'The reservations module is available on Pro and Enterprise plans. Upgrade to manage table bookings.',
      newReservation: 'New Reservation',
      editReservation: 'Edit Reservation',
      newReservationDescription: 'Create a new table reservation',
      editReservationDescription: 'Update reservation details',
      createReservation: 'Create Reservation',
      saveChanges: 'Save Changes',
      customerName: 'Customer Name',
      namePlaceholder: 'Enter customer name',
      phone: 'Phone',
      phonePlaceholder: '+1 234 567 890',
      email: 'Email',
      emailPlaceholder: 'email@example.com',
      date: 'Date',
      time: 'Time',
      partySize: 'Party Size',
      person: 'person',
      people: 'people',
      guests: 'guests',
      duration: 'Duration',
      tableNumber: 'Table Number',
      tablePlaceholder: 'e.g., Table 5, Patio A',
      table: 'Table',
      specialRequests: 'Special Requests',
      specialRequestsPlaceholder: 'Any dietary requirements, celebrations, etc.',
      internalNotes: 'Internal Notes',
      internalNotesPlaceholder: 'Staff-only notes',
      notes: 'Notes',
      today: 'Today',
      listTitle: 'Reservations for',
      filterByStatus: 'Filter by status',
      noReservations: 'No Reservations',
      noReservationsDescription: 'No reservations found for the selected date.',
      created: 'Reservation Created',
      createdDescription: 'The reservation has been created successfully.',
      updated: 'Reservation Updated',
      updatedDescription: 'The reservation has been updated.',
      deleted: 'Reservation Deleted',
      deletedDescription: 'The reservation has been removed.',
      deleteTitle: 'Delete Reservation?',
      deleteDescription: 'This action cannot be undone. The reservation will be permanently removed.',
      confirm: 'Confirm',
      markSeated: 'Mark as Seated',
      markCompleted: 'Mark as Completed',
      markNoShow: 'Mark as No-Show',
      cancel: 'Cancel',
      statusUpdated: 'Status Updated',
      statusPending: 'Marked as pending',
      statusConfirmed: 'Reservation confirmed',
      statusSeated: 'Guest seated',
      statusCompleted: 'Reservation completed',
      statusCancelled: 'Reservation cancelled',
      statusNoShow: 'Marked as no-show',
      status: {
        pending: 'Pending',
        confirmed: 'Confirmed',
        seated: 'Seated',
        completed: 'Completed',
        cancelled: 'Cancelled',
        noShow: 'No Show'
      },
      stats: {
        total: 'Total',
        pending: 'Pending',
        confirmed: 'Confirmed',
        seated: 'Seated',
        completed: 'Completed',
        cancelledNoShow: 'Cancelled/No-Show'
      },
      // Branch selector and booking link
      publicBookingLink: 'Public Booking Link',
      shareWithCustomers: 'Share this link with customers to let them book tables online',
      linkCopied: 'Link Copied',
      linkCopiedDescription: 'Public booking link copied to clipboard',
      // Table selection
      selectTable: 'Select a table',
      noTablePreference: 'No preference',
      tableCapacityNote: 'Showing tables that can seat {{count}} or more guests'
    },
    publicReservation: {
      title: 'Book a Table',
      subtitle: 'Fill in your details to make a reservation',
      notFound: 'Restaurant Not Found',
      notFoundDescription: 'The restaurant you are looking for does not exist or is no longer accepting reservations.',
      success: 'Reservation Submitted!',
      successSubtitle: 'We\'ll confirm your booking shortly',
      successDescription: 'Your reservation request has been received. We will confirm it shortly.',
      confirmationNote: 'You will receive a confirmation via phone or email once your reservation is approved.',
      preferredTable: 'Preferred Table',
      anyTable: 'Any available table',
      seatsCount: '{{count}} seats',
      error: 'Failed to submit reservation. Please try again.',
      submit: 'Book Table',
      footer: 'Powered by Ripplefy',
      location: 'Location',
      // Branch selection
      selectBranch: 'Select a Location',
      chooseBranchDescription: 'Choose which location you\'d like to visit',
      // New step-based booking keys
      howManyGuests: 'How many guests?',
      selectPartySize: 'Select the number of people in your party',
      selectDate: 'Select a Date',
      forGuests: 'For {{count}} guests',
      selectTime: 'Select a Time',
      selectTable: 'Choose a Table',
      tableOptional: 'Optional - or let us choose for you',
      letUsChoose: 'Let us pick the best table for you',
      yourDetails: 'Your Details',
      howToReachYou: 'How can we reach you?',
      reviewBooking: 'Review Booking',
      confirmBooking: 'Confirm Booking',
      reviewDetails: 'Review your reservation details',
      table: 'Table',
      confirmAndBook: 'Confirm & Book'
    },
    security: {
      twoFactor: {
        title: 'Two-Factor Authentication',
        description: 'Protect your account with an additional layer of security using an authenticator app',
        upgradeDescription: 'Add an extra layer of security to your account with two-factor authentication',
        upgradePrompt: 'Two-factor authentication is available on Pro and Enterprise plans. Upgrade to enable this security feature.',
        notSetup: 'Two-factor authentication is not yet enabled. We recommend enabling it for enhanced security.',
        setupInfo: 'You will need an authenticator app like Google Authenticator, Authy, or 1Password to set up 2FA.',
        setupButton: 'Set Up Two-Factor Authentication',
        enabled: 'Two-factor authentication is enabled',
        enrolledDevices: 'Enrolled Devices',
        authenticatorApp: 'Authenticator App',
        addedOn: 'Added on',
        recommendation: 'We recommend keeping 2FA enabled and setting up a backup authenticator on a different device.',
        setupTitle: 'Set Up Two-Factor Authentication',
        setupSteps: 'Scan the QR code with your authenticator app, then enter the verification code',
        cantScan: "Can't scan? Enter this code manually:",
        enterCode: 'Enter verification code',
        disableTitle: 'Disable Two-Factor Authentication?',
        disableWarning: 'This will remove the additional layer of security from your account. Are you sure you want to continue?',
        disableRisk: 'Disabling 2FA makes your account more vulnerable to unauthorized access. We strongly recommend keeping it enabled.',
        disableButton: 'Disable 2FA'
      },
      changePassword: {
        title: 'Change Password',
        description: 'Update your password to keep your account secure'
      },
      accountSecurity: {
        title: 'Account Security',
        description: 'Manage your account security settings',
        tip: 'Keep your account secure by using a strong password and signing out from devices you no longer use.'
      },
      signOutAll: 'Sign Out From All Devices',
      signOutAllDescription: "This will sign you out from all devices except this one. You'll need to sign in again on other devices."
    },
    settings: {
      title: 'Settings',
      description: 'Manage your account and application preferences',
      tabs: {
        usage: 'Usage',
        profile: 'Profile',
        notifications: 'Notifications',
        security: 'Security',
        delivery_zones: 'Delivery Zones',
        printers: 'Printers',
        general: 'General'
      },
      no_active_branch: 'No Active Branch',
      create_branch_for_delivery: 'Please create and activate a branch first to manage delivery zones.',
      create_branch_for_printers: 'Please create and activate a branch first to configure printers.'
    },
    common: {
      loading: 'Loading...',
      upgrade_now: 'Upgrade Now',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      remove: 'Remove',
      edit: 'Edit',
      add: 'Add',
      view: 'View',
      back: 'Back',
      close: 'Close',
      confirm: 'Confirm',
      apply: 'Apply',
      branch: 'Branch',
      search: 'Search',
      filter: 'Filter',
      all: 'All',
      none: 'None',
      yes: 'Yes',
      no: 'No',
      error: 'Error',
      success: 'Success',
      warning: 'Warning',
      info: 'Info',
      export: 'Export',
      enabled: 'Enabled',
      proPlan: 'Pro',
      verify: 'Verify',
      verifying: 'Verifying...',
      sending: 'Sending...',
      keepEnabled: 'Keep Enabled',
      disabling: 'Disabling...',
      done: 'Done'
    },
    branding: {
      powered_by: 'Powered by',
      tagline: 'Restaurant Management Made Simple'
    },
    backgroundJobs: {
      title: 'Background Jobs',
      activeJobs: 'Active Jobs',
      jobHistory: 'History',
      noActiveJobs: 'No active jobs',
      noJobHistory: 'No job history',
      cancel: 'Cancel',
      download: 'Download',
      started: 'Started',
      completed: 'Completed',
      failed: 'Failed',
      pending: 'Pending',
      processing: 'Processing',
      cancelled: 'Cancelled',
      jobTypes: {
        pnl_report: 'P&L Report',
        analytics_export: 'Analytics Export',
        order_history_export: 'Order History Export',
        inventory_report: 'Inventory Report',
        customer_export: 'Customer Export',
        menu_export: 'Menu Export',
        sales_report: 'Sales Report',
        staff_report: 'Staff Report',
        bulk_import: 'Bulk Import'
      }
    },
    analytics: {
      title: 'Analytics Dashboard',
      subtitle: 'Performance overview and insights',
      totalRevenue: 'Total Revenue',
      totalOrders: 'Total Orders',
      averageOrderValue: 'Average Order Value',
      completionRate: 'Completion Rate',
      ordersLast30Days: 'Orders in the last 30 days',
      perOrderAverage: 'Per order average',
      ofOrdersCompleted: 'of orders completed',
      fromLastPeriod: 'from last period',
      revenueOverTime: 'Revenue Over Time',
      revenueDescription: 'Daily revenue for the selected period',
      topPerformingItems: 'Top Performing Menu Items',
      topItemsDescription: 'Best-selling items by revenue in the last 30 days',
      quickInsights: 'Quick Insights',
      keyPerformanceIndicators: 'Key performance indicators',
      noRevenueData: 'No revenue data available for the selected period',
      noMenuItemData: 'No menu item data available',
      noBranchesAvailable: 'No Branches Available',
      noBranchAccessStaff: "You don't have access to any branches for analytics.",
      noBranchAccessOwner: 'Please create a branch to view analytics.',
      loadingAnalytics: 'Loading analytics...',
      errorLoadingSummary: 'Error loading summary data',
      week: 'Week',
      month: 'Month',
      revenue: 'Revenue',
      quantitySold: 'Quantity Sold',
      revenueTrend: 'Revenue Trend',
      revenueGrowing: 'Revenue is growing by',
      revenueDeclining: 'Revenue is declining by',
      comparedToPrevious: 'compared to the previous period.',
      orderCompletion: 'Order Completion',
      ordersCompletedSuccessfully: 'of orders are being completed successfully.',
      // New mobile-optimized keys
      total: 'total',
      noData: 'No data available',
      isTopItem: 'is the top seller',
      sold: 'sold',
      items: 'items',
      filters: 'Filters',
      timePeriod: 'Time Period'
    },
    // Desktop sync status
    sync: {
      connectionStatus: 'Connection',
      online: 'Online',
      offline: 'Offline',
      lastSync: 'Last synced',
      never: 'Never',
      justNow: 'Just now',
      minutesAgo: '{{count}} min ago',
      hoursAgo: '{{count}} hour ago',
      pendingChanges: 'Pending changes',
      syncNow: 'Sync Now',
      refreshMenu: 'Refresh Menu',
      updateAvailable: 'Update Available',
      downloading: 'Downloading...',
      downloadUpdate: 'Download Update',
      installUpdate: 'Install & Restart',
      syncError: 'Sync Error',
      syncComplete: 'Sync Complete',
      syncedItems: '{{count}} items synced',
      failedItems: '{{count}} items failed',
      retrying: 'Retrying...'
    },
    offline: {
      youAreOffline: 'You are offline',
      desktopMode: '— Desktop mode active',
      pendingSync: '{{count}} changes pending sync',
      pendingChanges: '{{count}} pending',
      syncing: 'Syncing...',
      synced: 'Synced!',
      syncNow: 'Sync Now',
      offline: 'Offline',
      poorConnection: 'Poor connection',
      retry_sync: 'Retry sync',
      menuNotCachedTitle: 'Offline ordering needs a cached menu',
      menuNotCachedDescription: 'Connect and cache categories and items for this branch before creating offline orders.',
      menuNotCachedAction: 'Cache menu for offline',
      menuNotCachedHint: 'Connect to the internet to cache the menu.',
      menuNotCachedToast: 'Menu is not cached for offline use. Connect and prefetch, then try again.',
      menuPrefetchSuccessTitle: 'Menu cached',
      menuPrefetchSuccess: 'Menu data saved for offline use.',
      menuPrefetchErrorTitle: 'Unable to cache menu',
      menuPrefetchError: 'Failed to cache menu. Check your connection and try again.',
      prefetching: 'Caching...',
      sync_status: {
        synced: 'Synced',
        syncing: 'Syncing...',
        pending: 'Pending sync',
        failed: 'Sync failed',
        offline: 'Created offline'
      },
      conflict: {
        title: 'Sync Conflicts Detected',
        description: 'Some data was modified both locally and on the server. Choose how to resolve each conflict.',
        entity: {
          order: 'Order',
          order_item: 'Order Item',
          category: 'Category',
          menu_item: 'Menu Item'
        },
        local: 'Local (Your Device)',
        server: 'Server (Latest)',
        keep_mine: 'Keep Mine',
        use_server: 'Use Server',
        keep_all_local: 'Keep All Local',
        use_all_server: 'Use All Server',
        apply_selected: 'Apply Selected'
      }
    },
    menuBuilder: {
    title: 'Menu Builder',
    blocks: 'Blocks',
    clickToAdd: 'Click to add to canvas',
    selectBranch: 'Please select a branch to use the Menu Builder',
    categories: 'Categories',
    noCategories: 'No categories available',
    
    categoryNav: {
      sticky: 'Sticky Position',
      style: 'Style',
      pills: 'Pills (Rounded)',
      chips: 'Chips (Square)',
      buttons: 'Buttons (Bordered)',
      size: 'Size',
      small: 'Small',
      medium: 'Medium',
      large: 'Large',
      iconSize: 'Icon Size',
      showIcons: 'Show Icons',
      showNames: 'Show Names',
      scrollToCategory: 'Scroll to Category',
      backgroundColor: 'Background Color',
      activeColor: 'Active Color',
    },
    
    // Block descriptions
    blockDesc: {
      header: 'Restaurant branding - logo and name',
      search: 'Search bar for filtering items',
      category: 'Display a category with its items',
      featured: 'Highlight specific menu items',
      banner: 'Promotional banner or image',
      text: 'Custom text or announcements',
      divider: 'Visual separator between sections',
      spacer: 'Add vertical whitespace'
    },
    
    // Canvas
    emptyCanvas: 'Start Building Your Menu',
    emptyCanvasHint: 'Click a block from the toolbox to add it here',
    noBlocks: 'No blocks to preview',
    
    // Toolbar
    mobile: 'Mobile',
    tablet: 'Tablet',
    preview: 'Preview',
    previewHint: 'Preview how customers will see the menu',
    publish: 'Publish',
    published: 'Published',
    unsaved: 'Unsaved',
    unsavedChanges: 'You have unsaved changes. Are you sure you want to leave?',
    
    // Properties Panel
    noBlockSelected: 'No block selected',
    selectBlockToEdit: 'Select a block to edit its properties',
    displayOptions: 'Display Options',
    showOptions: 'Show Options',
    
    // Header Block
    showLogo: 'Show Logo',
    showName: 'Show Name',
    logoSize: 'Logo Size',
    nameStyle: 'Name Style',
    alignment: 'Alignment',
    minimal: 'Minimal',
    bold: 'Bold',
    elegant: 'Elegant',
    withCover: 'With Cover',
    logoOnly: 'Logo Only',
    simple: 'Simple',
    logoPadding: 'Padding',
    
    // Category Block
    selectCategory: 'Select Category',
    chooseCategory: 'Choose a category...',
    displayMode: 'Display Mode',
    grid: 'Grid',
    list: 'List',
    compact: 'Compact',
    columns: 'Columns',
    maxItems: 'Max Items (0 = all)',
    categoryTitle: 'Category Title',
    images: 'Images',
    prices: 'Prices',
    descriptions: 'Descriptions',
    categoryNotFound: 'Category not found',
    noItems: 'No items in this category',
    
    // Featured Block
    sectionTitle: 'Section Title',
    titlePlaceholder: "e.g., Chef's Specials",
    selectedItems: 'Selected Items',
    noItemsSelected: 'No items selected',
    addItems: 'Add Items',
    selectItems: 'Select Items',
    displayStyle: 'Display Style',
    carousel: 'Carousel',
    highlight: 'Highlight',
    showBadge: 'Show Badge',
    badgeText: 'Badge Text',
    badgePlaceholder: 'e.g., Popular',
    
    // Banner Block
    imageUrl: 'Image URL',
    imageUrlHint: 'Use a direct link to an image',
    subtitle: 'Subtitle',
    subtitlePlaceholder: 'Optional subtitle...',
    bannerTitlePlaceholder: 'Banner title...',
    backgroundColor: 'Background Color',
    height: 'Height',
    overlayOpacity: 'Overlay Opacity',
    configureBanner: 'Configure banner in properties',
    
    // Text Block
    content: 'Content',
    textPlaceholder: 'Enter your text here...',
    textStyle: 'Text Style',
    body: 'Body',
    heading: 'Heading',
    caption: 'Caption',
    quote: 'Quote',
    enterText: 'Enter text in properties',
    
    // Divider Block
    dividerStyle: 'Divider Style',
    line: 'Line',
    dots: 'Dots',
    icon: 'Icon',
    color: 'Color',
    defaultColor: 'Default',
    
    // Spacer Block
    spacerHeight: 'Spacer Height',
    
    // Search Block
    placeholder: 'Placeholder Text',
    searchPlaceholder: 'Search menu...',
    searchStyle: 'Search Style',
    rounded: 'Rounded',
    square: 'Square',
    
    // Common
    layoutMode: 'Layout Mode',
    itemShape: 'Item Shape',
    fontSize: 'Font Size',
    textColor: 'Text Color',
    titleStyling: 'Title Styling',
    customSizePx: 'Custom Size (px)',
    default: 'Default',
    itemTextColors: 'Item Text Colors',
    itemNameColor: 'Item Name Color',
    itemDescriptionColor: 'Description Color',
    itemPriceColor: 'Price Color',
    
    // Sizes
    sizes: {
      sm: 'Small',
      md: 'Medium',
      lg: 'Large',
      xl: 'Extra Large',
      '2xl': '2x Large',
      custom: 'Custom'
    },
    
    // Shapes
    shapes: {
      rounded: 'Rounded',
      square: 'Square',
      pill: 'Pill',
      leaf: 'Leaf',
      arch: 'Arch'
    },
    
    // Alignment
    align: {
      left: 'Left',
      center: 'Center',
      right: 'Right'
    },
    
    // Legacy (keeping for backward compatibility)
    toolbox: {
      title: 'Toolbox',
      subtitle: 'Drag items to the canvas',
      header: 'Header',
      category: 'Category',
      carousel: 'Carousel',
      banner: 'Banner',
      textBlock: 'Text Block',
      searchBar: 'Search Bar',
      spacer: 'Spacer'
    },
    properties: {
      globalSettings: 'Global Settings',
      selectCategory: 'Select Category',
      chooseCategory: 'Choose a category',
      showTitle: 'Show Title',
      showImages: 'Show Images',
      itemsVisibility: 'Items Visibility',
      noItems: 'No items in this category',
      itemReordering: 'Item Reordering',
      itemReorderingTip: 'To reorder items within this category, use the "Edit Items" mode on the canvas block itself.'
    },
    canvas: {
      dragHere: 'Drag blocks here',
      selectCategoryPrompt: 'Select a category in properties'
    },
    actions: {
      save: 'Save',
      saving: 'Saving...',
      unsavedChanges: 'Unsaved changes',
      savedSuccessfully: 'Menu layout saved',
      saveFailed: 'Failed to save menu layout'
    },
    alerts: {
      selectBranch: 'Please select a branch from the top navigation to use the Menu Builder.'
    }
  },
  
    // Figma-like Menu Designer
    menuDesigner: {
      title: 'Menu Designer',
      selectBranch: 'Please select a branch to use the Menu Designer.',
      import: 'Import',
      export: 'Export',
      saving: 'Saving...',
      save: {
        button: 'Save',
        success: 'Design saved',
        successDescription: 'Your menu design has been saved successfully.',
        error: 'Failed to save design'
      },
      tools: {
        select: 'Select',
        pan: 'Pan (Hand)',
        text: 'Text',
        rectangle: 'Rectangle',
        circle: 'Circle',
        line: 'Line',
        image: 'Image',
        menuItem: 'Menu Item'
      },
      actions: {
        undo: 'Undo',
        redo: 'Redo',
        zoomIn: 'Zoom In',
        zoomOut: 'Zoom Out',
        fitToScreen: 'Fit to Screen'
      },
      view: {
        grid: 'Toggle Grid',
        rulers: 'Toggle Rulers',
        layers: 'Toggle Layers',
        properties: 'Toggle Properties'
      },
      layers: {
        title: 'Layers',
        empty: 'No elements yet. Add elements using the toolbar.',
        elements: 'elements'
      },
      properties: {
        title: 'Properties',
        noSelection: 'Select an element to edit its properties.',
        design: 'Design',
        position: 'Position',
        size: 'Size',
        rotation: 'Rotation',
        opacity: 'Opacity',
        text: 'Text',
        font: 'Font',
        fontSize: 'Font Size',
        style: 'Style',
        color: 'Color',
        fill: 'Fill',
        stroke: 'Stroke',
        strokeWidth: 'Stroke Width',
        borderRadius: 'Border Radius',
        imageUrl: 'Image URL',
        fit: 'Fit Mode',
        variant: 'Variant',
        backgroundColor: 'Background',
        priceColor: 'Price Color'
      },
      assets: {
        title: 'Assets',
        subtitle: 'Drag elements to customize your menu',
        items: 'Items',
        headers: 'Headers',
        bg: 'BG',
        categories: 'Categories',
        images: 'Media',
        noItems: 'No items',
        noCategories: 'No categories found',
        itemsHelp: 'Click items to add. Expand for separate elements.',
        categoriesHelp: 'Click to add a category header',
        uploadImage: 'Upload Image',
        clickToUpload: 'Click to upload',
        imageUrl: 'Image URL',
        quickAdd: 'Quick Add',
        addTitle: 'Title',
        addText: 'Text',
        addBorder: 'Border',
        addDivider: 'Divider',
        addBox: 'Box',
        addElement: 'Add element separately:',
        fullCard: 'Full Card',
        nameOnly: 'Name',
        priceOnly: 'Price',
        descOnly: 'Description',
        imageOnly: 'Image Only',
        restaurantLogo: 'Restaurant Logo',
        uploadLogo: 'Upload your logo',
        headerElements: 'Header Elements',
        restaurantName: 'Restaurant Name',
        menuTitle: 'Our Menu',
        tagline: 'Delicious food, made with love',
        addRestaurantName: 'Restaurant Name',
        addMenuTitle: 'Menu Title',
        addTagline: 'Tagline / Slogan',
        categoryHeaders: 'Category Headers'
      },
      background: {
        title: 'Background',
        color: 'Color',
        gradient: 'Gradient',
        image: 'Image',
        gradientHint: 'Click to apply gradient',
        uploadHint: 'Click to upload background image',
        patterns: 'Patterns',
        preview: 'Current Background',
        currentImage: 'Current Image'
      }
    }
  }
};